import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
// Using local worker file from public directory for reliable loading
// This ensures consistent behavior in Vite dev server, Playwright tests, and production builds
// The worker file is served by Vite from /public/pdf.worker.min.mjs
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

console.log('[pdfUtils] PDF.js worker configured:', pdfjsLib.GlobalWorkerOptions.workerSrc);

export interface PDFPageImage {
  pageNumber: number;
  imageDataUrl: string;
  width: number;
  height: number;
}

/**
 * Wraps a promise with a timeout
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMessage - Error message to throw on timeout
 * @returns Promise that rejects if timeout is exceeded
 */
function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    ),
  ]);
}

/**
 * Converts a PDF file to an array of images (one per page)
 * @param file - The PDF file to convert
 * @param scale - The scale factor for rendering (default: 2 for high quality)
 * @returns Array of image data URLs, one for each page
 */
export async function convertPdfToImages(
  file: File,
  scale: number = 2
): Promise<PDFPageImage[]> {
  console.log('[convertPdfToImages] Starting PDF conversion', {
    fileName: file.name,
    fileSize: file.size,
    scale
  });

  try {
    // Read file as ArrayBuffer
    console.log('[convertPdfToImages] Reading file as ArrayBuffer...');
    const arrayBuffer = await file.arrayBuffer();
    console.log('[convertPdfToImages] ArrayBuffer size:', arrayBuffer.byteLength);

    // Load the PDF document with timeout
    console.log('[convertPdfToImages] Loading PDF document...');
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await withTimeout(
      loadingTask.promise,
      30000, // 30 second timeout
      'PDF loading timed out. The file may be too large or corrupted.'
    );
    const pageCount = pdf.numPages;
    console.log('[convertPdfToImages] PDF loaded successfully. Pages:', pageCount);

    const images: PDFPageImage[] = [];

    // Convert each page to an image
    for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
      console.log(`[convertPdfToImages] Processing page ${pageNum}/${pageCount}...`);

      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      console.log(`[convertPdfToImages] Page ${pageNum} viewport:`, {
        width: viewport.width,
        height: viewport.height
      });

      // Create canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Could not get canvas context');
      }

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render PDF page to canvas
      // pdfjs-dist 5 makes `canvas` required. Rendering through canvasContext is
      // its documented backwards-compatible path, which requires canvas: null —
      // this is exactly what ran before, just stated explicitly.
      const renderContext = {
        canvas: null,
        canvasContext: context,
        viewport: viewport,
      };

      console.log(`[convertPdfToImages] Rendering page ${pageNum} to canvas...`);
      await withTimeout(
        page.render(renderContext).promise,
        15000, // 15 second timeout per page
        `Page ${pageNum} rendering timed out`
      );
      console.log(`[convertPdfToImages] Page ${pageNum} rendered successfully`);

      // Convert canvas to data URL
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
      console.log(`[convertPdfToImages] Page ${pageNum} converted to data URL (${imageDataUrl.length} bytes)`);

      images.push({
        pageNumber: pageNum,
        imageDataUrl,
        width: viewport.width,
        height: viewport.height,
      });
    }

    console.log('[convertPdfToImages] All pages converted successfully. Total images:', images.length);
    return images;
  } catch (error) {
    console.error('[convertPdfToImages] Error converting PDF to images:', error);
    console.error('[convertPdfToImages] Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      name: error instanceof Error ? error.name : 'Unknown',
      stack: error instanceof Error ? error.stack : 'No stack trace',
    });
    throw new Error('Failed to process PDF file. The file may be corrupted or invalid.');
  }
}

/**
 * Validates if a file is a PDF
 * @param file - The file to validate
 * @returns true if the file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Gets the first page of a PDF as an image for preview
 * @param file - The PDF file
 * @param scale - The scale factor for rendering (default: 1.5)
 * @returns Image data URL of the first page
 */
export async function getPdfFirstPagePreview(
  file: File,
  scale: number = 1.5
): Promise<string> {
  const images = await convertPdfToImages(file, scale);
  if (images.length === 0) {
    throw new Error('PDF has no pages');
  }
  return images[0].imageDataUrl;
}

/**
 * Extracts text content from a PDF file
 * @param file - The PDF file
 * @returns Array of text content, one for each page
 */
export async function extractTextFromPdf(file: File): Promise<string[]> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await withTimeout(
      loadingTask.promise,
      30000, // 30 second timeout
      'PDF loading timed out while extracting text'
    );
    const pageCount = pdf.numPages;

    const textContent: string[] = [];

    for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const pageText = content.items
        // Items are TextItem | TextMarkedContent; only TextItem carries text.
        // (Marked-content items used to map to undefined, which join() also
        // rendered as an empty string.)
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ');
      textContent.push(pageText);
    }

    return textContent;
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    return [];
  }
}

/**
 * Gets the number of pages in a PDF
 * @param file - The PDF file
 * @returns Number of pages
 */
export async function getPdfPageCount(file: File): Promise<number> {
  console.log('[getPdfPageCount] Getting page count for:', file.name);
  try {
    const arrayBuffer = await file.arrayBuffer();
    console.log('[getPdfPageCount] ArrayBuffer size:', arrayBuffer.byteLength);
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await withTimeout(
      loadingTask.promise,
      30000, // 30 second timeout
      'PDF loading timed out while getting page count'
    );
    const pageCount = pdf.numPages;
    console.log('[getPdfPageCount] Page count:', pageCount);
    return pageCount;
  } catch (error) {
    console.error('[getPdfPageCount] Error getting PDF page count:', error);
    console.error('[getPdfPageCount] Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      name: error instanceof Error ? error.name : 'Unknown',
    });
    throw new Error('Failed to read PDF file');
  }
}
